import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime, getArrivalMinutes } from '../../utils/helpers';
import Shirt_overlay from '../../images/Shirt_overlay.png';
import { FaClock, FaTrophy, FaStar, FaFutbol } from 'react-icons/fa';
import '../../scss/match/matchCard.scss';
import EditMatchModal from './EditMatchModal';

import api from '../../services/apiService';
import { authService } from '../../services/authService';
import { useNotification } from '../../components/NotificationProvider';
import { notifyAvailabilityResponseSaved } from '../../services/pushPermissionReminders';

const MatchCard = ({ match, onMatchDeleted, disableNavigation }) => {
    const navigate = useNavigate();

    const isManager = authService.isManager();
    const isCoach = sessionStorage.getItem('user_type') === 'coach';
    const isParent = authService.isParent();
    const shirt_number = sessionStorage.getItem('shirt_number');
    const teamType = sessionStorage.getItem('selected_team_type');
    const currentTeamName = sessionStorage.getItem('selected_team_name');
    const { showSuccess, showError } = useNotification();
    
    // Get child's name for parent users - just first name
    const childName = sessionStorage.getItem('child_name') || sessionStorage.getItem('first_name') || 'your child';
    const childFirstName = childName.split(' ')[0]; // Get just the first name
    
    // Get team configuration from sessionStorage
    const hasMaxPlayers = sessionStorage.getItem('selected_team_has_max_players') !== 'false';
    const hasWaitinglist = sessionStorage.getItem('selected_team_has_waitinglist') !== 'false';
    
    // Create full match datetime by combining date and time
    const matchDateTime = new Date(`${match.match_date}T${match.match_time}`);
    const now = new Date();
    
    // Check if match hasn't started yet
    const isNotStarted = matchDateTime > now;
    
    // Convert both dates to start of day for accurate comparison
    const matchDate = new Date(match.match_date);
    matchDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const isUpcoming = matchDate >= today;
    const [showEditModal, setShowEditModal] = useState(false);
    const [localTotalYes, setLocalTotalYes] = useState(parseInt(match.total_yes) || 0);
    const [localTotalNo, setLocalTotalNo] = useState(parseInt(match.total_no) || 0);
    const [currentResponse, setCurrentResponse] = useState(match.response);

    // Current player ID from session storage
    const currentPlayerId = sessionStorage.getItem('player_id');
    
    // Define these variables once here and use throughout the component
    const maxAllowed = match.max_players || 12;
    
    // Use API-provided counts instead of calculating locally
    const activeYes = parseInt(match.active_yes) || 0;
    const waitlistYes = parseInt(match.waitlist_yes) || 0;
    const totalYes = parseInt(match.total_yes) || 0;
    

    
    // Parse active and waitlist player IDs
    const activePlayerIds = match.active_player_ids ? match.active_player_ids.split(',') : [];
    const waitlistPlayerIds = match.waitlist_player_ids ? match.waitlist_player_ids.split(',') : [];
    
    // Check if current player is in the active or waitlist arrays directly
    const isInActiveList = activePlayerIds.includes(currentPlayerId);
    const isInWaitlistArray = waitlistPlayerIds.includes(currentPlayerId);
    
    // Check if the player has responded yes
    const hasRespondedYes = currentResponse === 'Yes';
    
    // First check if team has waitlist enabled
    const teamHasWaitlist = hasWaitinglist;
    
    // Check if total yes responses exceed max players allowed
    const isOverMaxPlayers = totalYes > maxAllowed;
    
    // Check if API is sending the newer waitlist counts
    const hasAPIWaitlistCounts = typeof match.active_yes !== 'undefined' && typeof match.waitlist_yes !== 'undefined';
    
    // Primary check: Is the player on the waitlist array?
    const isInAPIWaitlist = isInWaitlistArray;
    
    // Secondary checks for backward compatibility
    const flaggedAsWaitlist = match.is_waitlist === "1" || match.is_waitlist === 1 || match.is_waitlist === true;
    const hasWaitlistPosition = match.waitlist_position && parseInt(match.waitlist_position) > 0;
    
    // Combined check for showing waitlist status - prioritize direct array membership check
    const currentPlayerIsWaitlisted = isInAPIWaitlist || flaggedAsWaitlist || hasWaitlistPosition;
    

    
    // Fallback check: Based on the logic, should they be on the waitlist?
    const shouldBeOnWaitlist = hasRespondedYes && teamHasWaitlist && (
        // Direct array check is most reliable
        isInWaitlistArray || 
        // If we have API waitlist counts, use them
        (hasAPIWaitlistCounts && waitlistYes > 0 && flaggedAsWaitlist) || 
        // Otherwise fall back to our calculation
        (!hasAPIWaitlistCounts && isOverMaxPlayers && !isInActiveList)
    );
    
    // Final determination if player is on waitlist


    // Use ref to track if component is mounted
    const mounted = useRef(true);
    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleResponse = async (e, response) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Prevent multiple rapid submissions
        if (e.target.disabled) {
            return;
        }
        
        // If changing from Yes to No, show confirmation
        if (currentResponse === 'Yes' && response === 'no') {
            // Different confirmation message based on team type
            const teamType = match.team_type;
            let confirmationMessage;
            
            if (teamType === 'academy') {
                confirmationMessage = 'Are you sure? We will notify the coach.';
            } else {
                // For friendly/squad teams, notify waitlisted players
                confirmationMessage = 'Are you sure you cant play? We will notify players in the group.';
            }
            
            const confirmed = window.confirm(confirmationMessage);
            if (!confirmed) {
                return;
            }
        }
        
        e.target.disabled = true;
        
        const formattedResponse = response.charAt(0).toUpperCase() + response.slice(1);
        
        // Only return early if response is the same AND we're not confirming a place from waitlist
        if (formattedResponse === currentResponse && !(activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes' && response === 'yes')) {
            return;
        }
        
        // Get current player's response before updating
        const currentPlayerResponse = currentResponse;
        
        // Calculate if this should be a waitlist response
        const currentYesCount = currentPlayerResponse === 'Yes' 
            ? localTotalYes - 1  // Subtract current player from count
            : localTotalYes;
        
        // Only use waitlist functionality if team has it enabled
        const isWaitlist = hasWaitinglist && response === 'yes' && currentYesCount >= maxAllowed;
        
        // Special case: confirming from waitlist should set waitlist to false
        const isConfirmingFromWaitlist = activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes' && response === 'yes';
        
        // Remove active class from both buttons
        const buttons = e.target.parentElement.querySelectorAll('.response-btn');
        buttons.forEach(btn => btn.classList.remove('active'));
        
        // Add active class to clicked button
        e.target.classList.add('active');
        
        // Update totals based on response change
        if (response === 'yes') {
            if (currentResponse === 'No') {
                setLocalTotalNo(prev => Math.max(0, parseInt(prev) - 1));
            }
            if (currentResponse !== 'Yes') {
                setLocalTotalYes(prev => parseInt(prev) + 1);
            }
        } else if (response === 'no') {
            if (currentResponse === 'Yes') {
                setLocalTotalYes(prev => Math.max(0, parseInt(prev) - 1));
            }
            if (currentResponse !== 'No') {
                setLocalTotalNo(prev => parseInt(prev) + 1);
            }
        }
        
        setCurrentResponse(formattedResponse);
        
        // Pass the calculated isWaitlist value
        match.onRespond(response, isWaitlist);
        
        const handleResponseUpdate = async (data) => {
            if (data.success) {
                notifyAvailabilityResponseSaved();
                // Show notification for response update
                if (response === 'yes') {
                    if (isWaitlist) {
                        showSuccess('You have been added to the waiting list. We\'ll let you know if a spot becomes available.', 5000);
                    } else {
                        showSuccess('Thanks for confirming! We look forward to seeing you at the match.', 5000);
                    }
                } else if (response === 'no') {
                    showSuccess('Response updated. We hope to see you at a future match.', 5000);
                }
                
                // We're now handling waitlist confirmation before this function is called
                
                if (data.notifications_sent > 0) {
                    // Show admin notification about messages sent
                    const notification = document.createElement('div');
                    notification.className = 'response-message info';
                    notification.textContent = `Notifications sent to ${data.notifications_sent} waitlisted player${data.notifications_sent !== 1 ? 's' : ''}.`;
                    document.body.appendChild(notification);

                    setTimeout(() => {
                        notification.classList.add('fade-out');
                        setTimeout(() => {
                            document.body.removeChild(notification);
                        }, 300);
                    }, 5000);
                }
            }
        };

        try {
            // Make the is_waitlist value very explicit when confirming from waitlist
            const is_waitlist_value = isConfirmingFromWaitlist ? "0" : (isWaitlist ? "1" : "0");
            
            const res = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    player_id: sessionStorage.getItem('player_id'),
                    response: response,
                    match_id: match.match_id,
                    is_waitlist: is_waitlist_value,
                    confirm_from_waitlist: isConfirmingFromWaitlist ? "1" : "0" // Add an extra flag to be absolutely clear
                })
            });
            
            const data = res.data;
            
            // Handle waitlist confirmation more directly
            if (isConfirmingFromWaitlist) {
                showSuccess('You have successfully confirmed your place in this match!', 5000);
                window.location.reload();
                return;
            }
            
            if (mounted.current) {  // Only process response if component is still mounted
                handleResponseUpdate(data);
            }
        } catch (error) {
            console.error('Error updating response:', error);
            showError('There was an error updating your response. Please try again.', 5000);
        } finally {
            setTimeout(() => {
                if (mounted.current) {  // Only re-enable if component is still mounted
                    e.target.disabled = false;
                }
            }, 5000);
        }
    };



    const handleMatchUpdated = (updatedMatch) => {
        // Implement any UI updates needed
        setShowEditModal(false);
    };

    // Calculate score including own goals
    const calculateScoreWithOwnGoals = () => {
        if (isNotStarted) return 'vs';
        
        // Calculate total scores including own goals
        let whiteScore = parseInt(match.score_white || '0');
        let colourScore = parseInt(match.score_colour || '0');
        
        // Add own goals to the opposing team's score if they exist
        if (match.own_goals) {
            // Own goals scored by white team add to colour team's score
            const whiteTeamName = match.team_a || (match.is_home ? (match.team_name || currentTeamName) : match.opposition) || 'White';
            // Own goals scored by colour team add to white team's score
            const colourTeamName = match.team_b || (match.is_home ? match.opposition : (match.team_name || currentTeamName)) || 'Colour';
            
            // Add own goals to opposing team scores
            if (match.own_goals[whiteTeamName] || match.own_goals['White']) {
                const ownGoals = match.own_goals[whiteTeamName] || match.own_goals['White'];
                if (Array.isArray(ownGoals)) {
                    ownGoals.forEach(og => {
                        if (og?.is_opposition) {
                            return;
                        }
                        colourScore += parseInt(og.own_goals || 0);
                    });
                }
            }
            
            if (match.own_goals[colourTeamName] || match.own_goals['Colour']) {
                const ownGoals = match.own_goals[colourTeamName] || match.own_goals['Colour'];
                if (Array.isArray(ownGoals)) {
                    ownGoals.forEach(og => {
                        if (og?.is_opposition) {
                            return;
                        }
                        whiteScore += parseInt(og.own_goals || 0);
                    });
                }
            }
        }
        
        // For friendly matches, always show white team score first, regardless of home/away
        if (teamType === 'friendly') {
            return `${whiteScore} - ${colourScore}`;
        }
        
        // For academy and squad matches, determine if we're home or away
        const isHome = match.is_home === undefined ? true : match.is_home === '1' || match.is_home === true;
        
        // Show score order based on home/away status for academy/squad matches
        // When home: our team first (whiteScore - colourScore)
        // When away: opposition first (colourScore - whiteScore)
        if (isHome) {
            return `${whiteScore} - ${colourScore}`;
        } else {
            return `${colourScore} - ${whiteScore}`;
        }
    };

    // Utility function to capitalize names
    const capitalizeNames = (nameString) => {
        if (!nameString || typeof nameString !== 'string') return '';
        
        // Split by commas for multiple names
        return nameString.split(',').map(name => {
            return name.trim().split(' ').map(part => {
                if (!part) return '';
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            }).join(' ');
        }).join(', ');
    };

    const oppositionOwnGoalsCount = parseInt(match.opposition_own_goals, 10) || 0;
    const cardIsHome = match.is_home === undefined ? true : match.is_home === '1' || match.is_home === true;
    const renderOppositionOwnGoal = () => {
        if (oppositionOwnGoalsCount <= 0) {
            return null;
        }
        return (
            <div key="opposition-own-goal" className="scorer own-goal">
                <span className="name">{match.opposition || 'Opposition'} (OG)</span>
                <span className="goals">
                    {Array.from({ length: oppositionOwnGoalsCount }, (_, i) => (
                        <FaFutbol key={i} className="goal-icon" />
                    ))}
                </span>
            </div>
        );
    };

    const renderTeams = () => {
        // Squad and Academy match display with team shields
        if (teamType === 'squad' || teamType === 'academy') {
            // Team IDs for home and away teams
            const teamAId = match.team_a_id || sessionStorage.getItem('selected_team_id');
            const teamBId = match.opposition_team_id || match.team_b_id;
            
            // Import default logos
            const defaultLightLogo = require('../../images/teams/logo-light.png');
            
            // Initialize with default paths
            let teamAShieldPath = defaultLightLogo;
            let teamBShieldPath = defaultLightLogo;
            
            // Try to load team-specific shields from API
            try {
                // For team A
                if (teamAId) {
                    teamAShieldPath = `https://football.pebbledesigns.co.uk/api/images/teams/${teamAId}/logo-dark.png`;
                }
                
                // For team B
                if (teamBId) {
                    teamBShieldPath = `https://football.pebbledesigns.co.uk/api/images/teams/${teamBId}/logo-dark.png`;
                }
            } catch (error) {
                // Error loading team shields, removed console.error
            }

            // Determine if we're home or away
            const isHome = match.is_home === undefined ? true : match.is_home === '1' || match.is_home === true;

            // Set up team names and shields based on home/away status
            const leftTeamShield = isHome ? teamAShieldPath : teamBShieldPath;
            const rightTeamShield = isHome ? teamBShieldPath : teamAShieldPath;
            const leftTeamName = isHome ? (match.team_a || match.team_name || currentTeamName || 'Home') : (match.team_b || match.opposition || 'Away');
            const rightTeamName = isHome ? (match.team_b || match.opposition || 'Away') : (match.team_a || match.team_name || currentTeamName || 'Home');
            
            // calculateScoreWithOwnGoals already orders the score to match the
            // swapped logos (opposition first when away). Do not swap again.
            const renderScore = () => {
                if (isNotStarted) return 'vs';
                return calculateScoreWithOwnGoals();
            };

            return (
                <div className={`match-score ${teamType === 'academy' ? 'academy-match' : 'squad-match'}`}>
                    <div className="team team-a">
                        <div className="shield-container">
                            <img 
                                className='team-shield' 
                                src={leftTeamShield} 
                                alt={leftTeamName}
                                onError={(e) => {
                                    e.target.src = defaultLightLogo;
                                }}
                            />
                        </div>
                        <div className="team-name">
                            {leftTeamName}
                        </div>
                    </div>
                    <div className="score">
                        {renderScore()}
                    </div>
                    <div className="team team-b">
                        <div className="shield-container">
                            <img 
                                className='team-shield' 
                                src={rightTeamShield} 
                                alt={rightTeamName}
                                onError={(e) => {
                                    e.target.src = defaultLightLogo;
                                }}
                            />
                        </div>
                        <div className="team-name">
                            {rightTeamName}
                        </div>
                    </div>
                </div>
            );
        }

        // Friendly match display with shirts (default)
        return (
            <div className="match-score">
                <div className="team team-white">
                    <div className="shirt-container">
                        <svg fill="#ffffff" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.2 445.73">
                            <g id="Layer_1-2" data-name="Layer 1">
                                <path className="cls-1" d="m199.37,4.47l4.04,3.33,3.8,2.38,2.61,4.28,36.36,15.68,22.1,8.55,14.26,5.94s14.73,7.84,17.58,10.93,14.73,19.01,14.73,19.01l11.88,21.62s1.9,8.55,1.9,9.27,2.61,24.48,2.61,24.48l.71,27.09.24,30.18-1.9,1.9-19.96.95h-24.95l-4.99-.48-2.85-3.09-.95,7.6-.24,70.81v115.25s0,59.17,0,59.17l-.95,2.85-4.75,2.14-4.51.24h-61.31s-70.34.71-70.34.71l-51.33-.48-24,.95-5.7-3.8-.71-4.75.71-199.13-.48-25.43-1.43-13.78-1.9-11.41-.95-4.28-1.9,2.61-.24,4.28-37.31.95-8.32-.48-.95-.95.95-52.51,2.85-31.13,6.42-21.39s5.23-13.54,9.27-18.53,14.26-14.73,14.26-14.73l56.55-25.43s17.58-6.18,22.81-11.17,7.37-8.32,7.37-8.32c0,0,19.72-5.7,24.24-6.18s30.18,0,37.78.71,16.87,3.56,16.87,3.56Z"/>
                            </g>
                        </svg>
                        <img className='shirt-overlay' src={Shirt_overlay} alt="White Team" />
                        {match.played_match && match.team_colour === 'White' && (
                            <span className="shirt-number">{shirt_number}</span>
                        )}
                    </div>
                    <div className={`team-name ${match.played_match && match.team_colour === 'White' ? 'played' : ''}`}>
                        {match.team_a || (match.is_home ? (match.team_name || currentTeamName) : match.opposition) || 'White'}
                    </div>
                </div>
                <div className="score">
                    {isNotStarted ? 'vs' : (() => {
                        const score = calculateScoreWithOwnGoals();
                        if (score === 'vs') return 'vs';
                        
                        // Always return the score as calculated - no swapping needed
                        // The calculateScoreWithOwnGoals function handles the correct ordering
                        return score;
                    })()}
                </div>
                <div className="team team-colour">
                    <div className="shirt-container">
                        <svg fill="#bd1313" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.2 445.73">
                            <g id="Layer_1-2" data-name="Layer 1">
                                <path className="cls-1" d="m199.37,4.47l4.04,3.33,3.8,2.38,2.61,4.28,36.36,15.68,22.1,8.55,14.26,5.94s14.73,7.84,17.58,10.93,14.73,19.01,14.73,19.01l11.88,21.62s1.9,8.55,1.9,9.27,2.61,24.48,2.61,24.48l.71,27.09.24,30.18-1.9,1.9-19.96.95h-24.95l-4.99-.48-2.85-3.09-.95,7.6-.24,70.81v115.25s0,59.17,0,59.17l-.95,2.85-4.75,2.14-4.51.24h-61.31s-70.34.71-70.34.71l-51.33-.48-24,.95-5.7-3.8-.71-4.75.71-199.13-.48-25.43-1.43-13.78-1.9-11.41-.95-4.28-1.9,2.61-.24,4.28-37.31.95-8.32-.48-.95-.95.95-52.51,2.85-31.13,6.42-21.39s5.23-13.54,9.27-18.53,14.26-14.73,14.26-14.73l56.55-25.43s17.58-6.18,22.81-11.17,7.37-8.32,7.37-8.32c0,0,19.72-5.7,24.24-6.18s30.18,0,37.78.71,16.87,3.56,16.87,3.56Z"/>
                            </g>
                        </svg>
                        <img className='shirt-overlay' src={Shirt_overlay} alt="Colour Team" />
                        {match.played_match && match.team_colour === 'Colour' && (
                            <span className="shirt-number">{shirt_number}</span>
                        )}
                    </div>
                    <div className={`team-name ${match.played_match && match.team_colour === 'Colour' ? 'played' : ''}`}>
                        {match.team_b || (match.is_home ? match.opposition : (match.team_name || currentTeamName)) || 'Colour'}
                    </div>
                </div>
            </div>
        );
    };

    const renderMotmSection = () => {
        // If match isn't played (no scores), don't show anything
        if (!match?.score_white && !match?.score_colour) {
            return null;
        }

        // Check if POTM values exist and are valid (not undefined/null/empty)
        const hasMotmNames = match?.motm_names && match.motm_names !== "undefined" && typeof match.motm_names === 'string' && match.motm_names.trim() !== "";
        const hasMotm = match?.motm && match.motm !== "undefined" && (
            (typeof match.motm === 'string' && match.motm.trim() !== "") || 
            (typeof match.motm === 'object' && match.motm.first_name)
        );
        const hasMotmFirstName = match?.motm_first_name && match.motm_first_name !== "undefined" && typeof match.motm_first_name === 'string' && match.motm_first_name.trim() !== "";
        
        // Only consider POTM closed if we have valid winner data
        const isMotmClosed = hasMotmNames || hasMotm || hasMotmFirstName;

        // If we have valid POTM winners, show them
        if (isMotmClosed) {
            // Determine the winner name to display
            let winnerName = "";
            if (hasMotmNames) {
                winnerName = capitalizeNames(match.motm_names);
            } else if (match.motm_name) {
                winnerName = capitalizeNames(match.motm_name);
            } else if (match.motm && typeof match.motm === 'object' && match.motm.first_name) {
                winnerName = capitalizeNames(`${match.motm.first_name} ${match.motm.last_name}`);
            } else if (hasMotmFirstName) {
                winnerName = capitalizeNames(`${match.motm_first_name} ${match.motm_last_name}`);
            }
            
            // Only render if we have a winner name
            if (winnerName && winnerName !== "undefined") {
                return (
                    <div className="motm">
                        <FaTrophy className="trophy-icon" />
                        <div className="motm-winners">
                            {winnerName}
                        </div>
                    </div>
                );
            }
            return null;
        } else if ((match.played_match || (match.score_white && match.score_colour)) && !isCoach && !match.isFinishedMatch) {
            // Show vote button if match is played OR has scores, no winners yet, user is not a coach, AND not marked as finished match
            // No need to wait 2 hours - if match has scores, it's finished and ready for voting
            return (
                <div className="motm-vote-button">
                    <button 
                        className="vote-motm-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/match/${match.match_id}/motm`);
                        }}
                    >
                        <FaStar /> Vote POTM <FaStar />
                    </button>
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <div className="match-card-container">
                <div 
                    className={`match-card ${isNotStarted ? 'future-game' : ''} ${teamType} ${match.status} ${match.status && match.status !== 'active' ? 'has-status-overlay' : ''}`}
                    onClick={(e) => {
                        if (disableNavigation) {
                            e.preventDefault();
                            return;
                        }
                        e.preventDefault();
                        navigate(`/match/${match.match_id}`);
                    }}
                >
                    {(match.status === 'cancelled' || match.status === 'postponed') && (
                        <div className={`status-stamp ${match.status}`}>
                            {match.status === 'cancelled' ? 'Cancelled' : 
                             match.status === 'postponed' ? 'Postponed' : ''}
                        </div>
                    )}

                    <div className="match-location">
                        {match.match_type || 'FRIENDLY'}
                        <div className="match-type">{match.location}</div>
                    </div>
                    {renderTeams()}
                    <div className="match-venue">
                        <div className="match-date">
                            {formatDate(match.match_date)}
                        </div>
                        <div className="match-time">
                            <div className="kickoff-time">
                                <FaClock />
                                {match.status === 'TBC' ? 'TBC' : `K.O. ${formatTime(match.match_time, match.status)}`}
                            </div>
                            {isNotStarted && match.status !== 'TBC' && (
                                <div className="arrival-note">Arrive {getArrivalMinutes(match)} minutes before</div>
                            )}
                        </div>
                    </div>
                    {match.scorers && (
                        <div className="scorers">
                            <div className="scorers-columns">
                                <div className="scorers-column">
                                    {match.scorers.White && match.scorers.White.length > 0 ? (
                                        match.scorers.White.map(scorer => (
                                            <div 
                                                key={`goal-${scorer.name}`} 
                                                className={`scorer ${scorer.name === match.player_name ? 'highlight' : ''}`}
                                            >
                                                <span className="name">{scorer.name}</span>
                                                <span className="goals">
                                                    {Array.from({ length: scorer.goals }, (_, i) => (
                                                        <FaFutbol key={i} className="goal-icon" />
                                                    ))}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        !(match.own_goals && match.own_goals.Colour && match.own_goals.Colour.length > 0) &&
                                        !(cardIsHome && oppositionOwnGoalsCount > 0) && (
                                        <div className="no-scorers">—</div>
                                        )
                                    )}
                                    {/* Own Goals scored by Colour Team players (appear in White team column) */}
                                    {match.own_goals && match.own_goals.Colour && match.own_goals.Colour.length > 0 && (
                                        match.own_goals.Colour.map(ownGoal => (
                                            <div 
                                                key={`own-goal-${ownGoal.name}`} 
                                                className={`scorer ${ownGoal.name === match.player_name ? 'highlight' : ''}`}
                                            >
                                                <span className="name">{ownGoal.name} (OG)</span>
                                                <span className="goals">
                                                    {Array.from({ length: ownGoal.own_goals }, (_, i) => (
                                                        <FaFutbol key={i} className="goal-icon" />
                                                    ))}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                    {cardIsHome && renderOppositionOwnGoal()}
                                </div>
                                <div className="scorers-column">
                                    {match.scorers.Colour && match.scorers.Colour.length > 0 ? (
                                        match.scorers.Colour.map(scorer => (
                                            <div 
                                                key={`goal-${scorer.name}`} 
                                                className={`scorer ${scorer.name === match.player_name ? 'highlight' : ''}`}
                                            >
                                                <span className="name">{scorer.name}</span>
                                                <span className="goals">
                                                    {Array.from({ length: scorer.goals }, (_, i) => (
                                                        <FaFutbol key={i} className="goal-icon" />
                                                    ))}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        !(match.own_goals && match.own_goals.White && match.own_goals.White.length > 0) &&
                                        !(!cardIsHome && oppositionOwnGoalsCount > 0) && (
                                        <div className="no-scorers">—</div>
                                        )
                                    )}
                                    {/* Own Goals scored by White Team players (appear in Colour team column) */}
                                    {match.own_goals && match.own_goals.White && match.own_goals.White.length > 0 && (
                                        match.own_goals.White.map(ownGoal => (
                                            <div 
                                                key={`own-goal-${ownGoal.name}`} 
                                                className={`scorer ${ownGoal.name === match.player_name ? 'highlight' : ''}`}
                                            >
                                                <span className="name">{ownGoal.name} (OG)</span>
                                                <span className="goals">
                                                    {Array.from({ length: ownGoal.own_goals }, (_, i) => (
                                                        <FaFutbol key={i} className="goal-icon" />
                                                    ))}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                    {!cardIsHome && renderOppositionOwnGoal()}
                                </div>
                            </div>
                        </div>
                    )}
                    {renderMotmSection()}
                    {/* Show response section for everyone except managers, but handle permissions separately */}
                    {isNotStarted && match.entry_open && !isManager && (
                        <div className="response-section">
                            {hasMaxPlayers && (
                                <>
                                    {((activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes') || 
                                     (localTotalYes >= match.max_players && currentResponse !== 'Yes') || 
                                     (localTotalYes >= match.max_players)) ? (
                                        <p className="max-players-notice">
                                            {activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes' ? (
                                                <>Position available! Click "Confirm Place" to move from the waitlist and secure your spot!</>
                                            ) : (
                                                <>
                                                    {localTotalYes >= match.max_players && (
                                                        <>
                                                            {currentResponse !== 'Yes' ? (
                                                                <>Maximum players reached!<br/>
                                                                {hasWaitinglist ? 
                                                                    'Respond "Yes" to be added to the waitlist and if a spot opens up we\'ll let you know!' :
                                                                    'Unfortunately, this team does not have a waiting list.'
                                                                }
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {currentPlayerIsWaitlisted ? (
                                                                        <>You are currently on the waitlist for this match. 
                                                                        <br/>We'll notify you if a place becomes available.</>
                                                                    ) : (
                                                                        <>Maximum players reached for this match!</>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </p>
                                    ) : null}
                                </>
                            )}
                            
                            <p>{isParent ? `Can ${childFirstName} play?` : 'Can you play?'}</p>
                            
                            {/* Show response buttons for all users except coaches, but disable for parents without permission */}
                            <div className="response-buttons">
                                <button 
                                    className={`response-btn yes ${
                                        activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes' 
                                        ? 'pulse-btn' 
                                        : (currentResponse === 'Yes' 
                                            ? 'active' 
                                            : currentResponse === 'No' ? 'inactive' : '')
                                    } ${authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(currentPlayerId)) ? 'disabled' : ''}`}
                                    onClick={(e) => {
                                        if (!authService.isChildBlockedFromResponding() && (!authService.isParent() || authService.canRespondToMatches(currentPlayerId))) {
                                            handleResponse(e, 'yes');
                                        }
                                    }}
                                    disabled={authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(currentPlayerId))}
                                >
                                    {activeYes < maxAllowed && currentPlayerIsWaitlisted && currentResponse === 'Yes' ? 'Confirm Place' : 'Yes'}
                                </button>
                                <button 
                                    className={`response-btn no ${
                                        currentResponse === 'No' 
                                            ? 'active' 
                                            : currentResponse === 'Yes' ? 'inactive' : ''
                                    } ${authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(currentPlayerId)) ? 'disabled' : ''}`}
                                    onClick={(e) => {
                                        if (!authService.isChildBlockedFromResponding() && (!authService.isParent() || authService.canRespondToMatches(currentPlayerId))) {
                                            handleResponse(e, 'no');
                                        }
                                    }}
                                    disabled={authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(currentPlayerId))}
                                >
                                    No
                                </button>
                            </div>
                            
                            {/* Show message for parents without permission */}
                            {authService.isParent() && !authService.canRespondToMatches(currentPlayerId) && (
                                <p className="permission-notice">
                                    You need coach approval to respond to matches for this child.
                                </p>
                            )}
                            {authService.isChildBlockedFromResponding() && (
                                <p className="permission-notice">
                                    Ask your parent to allow match responses in Child Access Settings.
                                </p>
                            )}
                            {/* Show responder information for parents and children */}
                            <div className="responder-info">
                                <small style={{ fontStyle: 'italic', color: '#666' }}>
                                    {currentResponse && match.responder_first_name && match.responder_last_name ? (
                                        <>
                                            {/* For academy teams, always show parent name. For friendly/squad, show based on user type */}
                                            {teamType === 'academy' ? (
                                                <>
                                                    Responded by {match.responder_first_name.charAt(0).toUpperCase() + match.responder_first_name.slice(1)} {match.responder_last_name.charAt(0).toUpperCase() + match.responder_last_name.slice(1)}
                                                    {match.response_updated ? (
                                                        <> on {new Date(match.response_updated).toLocaleDateString()} at {new Date(match.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    ) : (
                                                        <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    )}
                                                </>
                                            ) : isParent ? (
                                                <>
                                                    Responded by {match.responder_first_name.charAt(0).toUpperCase() + match.responder_first_name.slice(1)} {match.responder_last_name.charAt(0).toUpperCase() + match.responder_last_name.slice(1)}
                                                    {match.response_updated ? (
                                                        <> on {new Date(match.response_updated).toLocaleDateString()} at {new Date(match.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    ) : (
                                                        <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    Responded on {match.response_updated ? (
                                                        <>{new Date(match.response_updated).toLocaleDateString()} at {new Date(match.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    ) : (
                                                        <>{new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>Response needed</>
                                    )}
                                </small>
                            </div>
                        </div>
                    )}
                    {/* Only show response totals if match is upcoming AND not finished */}
                    {isUpcoming && 
                     !match.isFinishedMatch && 
                     !match.score_white && 
                     !match.score_colour && (
                        <div className="response-totals">
                            <div className="total yes">
                                <span className="count">
                                    <span className="current">{activeYes}</span>
                                    {hasMaxPlayers && (
                                        <span className="max">/ {maxAllowed}</span>
                                    )}
                                </span>
                                <span className="label">Yes</span>
                            </div>
                            <div className="total no">
                                <span className="count">{localTotalNo}</span>
                                <span className="label">No</span>
                            </div>
                            {hasWaitinglist && (
                                <div className="total waitlist">
                                    <span className="count">{waitlistYes}</span>
                                    <span className="label">W/List</span>
                                </div>
                            )}
                            <div className="total awaiting">
                                <span className="count">{match.total_awaiting || 0}</span>
                                <span className="label">Awaiting</span>
                            </div>
                        </div>
                    )}
                    {!isUpcoming && match.entry_open === null && (
                        <div className="response-section response-closed">
                            <p>Responses not yet open</p>
                        </div>
                    )}
                </div>
            </div>
            {showEditModal && (
                <EditMatchModal
                    match={match}
                    onMatchUpdated={handleMatchUpdated}
                    onClose={() => setShowEditModal(false)}
                />
            )}
        </>
    );
};

export default MatchCard;